import GetItemFromLocalstorage from "Utils/GetItemFromLocalstorage";
import { getSpPermissionStatus } from "helpers/RolesPermission";
import { isAiFeaturesEnabled } from "config/aiFeatureFlags";
import { AI_PERMISSIONS_PARENT } from "config/aiRolePermissions";
import { USER_ROLE } from "Constants/constant";

/** Matches backend App\Ai\Config\AiFeatures::MOD_TIMESHEET */
export const ORG_AI_MOD_TIMESHEET = "ai_timesheet";
export const ORG_AI_MOD_CARE_WATCH = "ai_care_watch";

/** Matches backend App\Ai\Config\AiFeatures::MOD_COPILOT */
export const ORG_AI_MOD_COPILOT = "ai_copilot";

/** Matches backend sub-feature keys on service_provider_settings.ai_features */
export const ORG_AI_SUB_CHECK_ANOMALIES = "check_timesheet_anomalies";
export const ORG_AI_SUB_BULK_CHECK_ANOMALIES = "bulk_check_timesheet_anomalies";
export const ORG_AI_SUB_ANOMALY_READ = "anomaly_read";

export const ROLE_AI_ANOMALY_CHECK = "ai.timesheet.anomaly_check";
export const ROLE_AI_BULK_ANOMALY_CHECK = "ai.timesheet.bulk_anomaly_check";
export const ROLE_AI_ANOMALY_READ = "ai.timesheet.anomaly_read";
export const ROLE_AI_CARE_WATCH = "ai.care_watch.view";

/** Matches backend AiFeaturePermissions map for MOD_COPILOT (_module). */
export const ROLE_AI_VERTEX360_COPILOT_QUERY = "ai.copilot.query";

export const SP_SETTINGS_AI_UPDATED_EVENT = "spSettingsAiUpdated";

const coerceBool = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    return ["1", "true", "yes", "on"].includes(value.trim().toLowerCase());
  }
  return Boolean(value);
};

/**
 * Canonical org timesheet flags used by resolveTimesheetAiAccess.
 *
 * @param {boolean} aiEnabled
 * @param {Record<string, unknown>|null|undefined} timesheetNode
 */
export const buildOrgTimesheetFlags = (aiEnabled, timesheetNode) => {
  const moduleEnabled = coerceBool(timesheetNode?.enabled);
  const checkEnabled = coerceBool(
    timesheetNode?.timesheet_check ?? timesheetNode?.[ORG_AI_SUB_CHECK_ANOMALIES]
  );
  const readEnabled = coerceBool(
    timesheetNode?.timesheet_insights ?? timesheetNode?.[ORG_AI_SUB_ANOMALY_READ]
  );
  const bulkEnabled = coerceBool(
    timesheetNode?.timesheet_bulk_check ??
      timesheetNode?.[ORG_AI_SUB_BULK_CHECK_ANOMALIES]
  );

  return {
    ai_enabled: aiEnabled,
    orgTimesheetModule: aiEnabled && moduleEnabled,
    orgCanAnalyse: aiEnabled && moduleEnabled && checkEnabled,
    // Every bulk chunk goes through the anomaly check, so it needs both.
    orgCanBulkAnalyse: aiEnabled && moduleEnabled && checkEnabled && bulkEnabled,
    orgCanViewInsights: aiEnabled && moduleEnabled && readEnabled,
    orgCareWatch: false,
  };
};

/**
 * Parse slim org payload (login service_provider or sp/ai/settings).
 *
 * @param {Record<string, unknown>|null|undefined} payload
 */
export const parseOrgAiPayload = (payload) => {
  const aiEnabled = coerceBool(payload?.ai_enabled);
  const timesheet = payload?.ai_org_permissions?.timesheet;
  const careWatch = payload?.ai_org_permissions?.care_watch;
  const flags = timesheet && typeof timesheet === "object"
    ? buildOrgTimesheetFlags(aiEnabled, timesheet)
    : parseOrgTimesheetAiFromLegacyFeatures(payload);

  return {
    ...flags,
    orgCareWatch: coerceBool(careWatch?.enabled) && flags.ai_enabled,
  };
};

/**
 * Legacy parser when only ai_features tree is present.
 *
 * @param {Record<string, unknown>|null|undefined} payload
 */
export const parseOrgTimesheetAiFromLegacyFeatures = (payload) => {
  const aiEnabled = coerceBool(payload?.ai_enabled);
  const features = payload?.ai_features || {};
  const timesheet =
    features[ORG_AI_MOD_TIMESHEET] && typeof features[ORG_AI_MOD_TIMESHEET] === "object"
      ? features[ORG_AI_MOD_TIMESHEET]
      : {};

  return buildOrgTimesheetFlags(aiEnabled, {
    enabled: timesheet.enabled,
    timesheet_check: timesheet[ORG_AI_SUB_CHECK_ANOMALIES],
    timesheet_bulk_check: timesheet[ORG_AI_SUB_BULK_CHECK_ANOMALIES],
    timesheet_insights: timesheet[ORG_AI_SUB_ANOMALY_READ],
  });
};

/** @deprecated Use parseOrgAiPayload */
export const parseOrgTimesheetAiFromSettings = parseOrgAiPayload;

/**
 * Canonical org copilot flags used by resolveVertex360CopilotAiAccess.
 *
 * @param {boolean} aiEnabled
 * @param {Record<string, unknown>|null|undefined} copilotNode
 */
export const buildOrgVertex360CopilotFlags = (aiEnabled, copilotNode) => {
  const moduleEnabled = coerceBool(copilotNode?.enabled);
  return {
    ai_enabled: aiEnabled,
    orgVertex360CopilotModule: aiEnabled && moduleEnabled,
  };
};

/**
 * Parse slim org payload for Copilot (login service_provider or sp/ai/settings).
 *
 * @param {Record<string, unknown>|null|undefined} payload
 */
export const parseOrgVertex360CopilotAiPayload = (payload) => {
  const aiEnabled = coerceBool(payload?.ai_enabled);
  const copilot = payload?.ai_org_permissions?.copilot;
  const node = copilot && typeof copilot === "object" ? copilot : {};
  return buildOrgVertex360CopilotFlags(aiEnabled, node);
};

/**
 * Org Copilot gates from localStorage (login service_provider or spSettings).
 * Returns null when no AI org payload is present yet.
 */
export const readOrgVertex360CopilotAiFromServiceProvider = () => {
  const spSettings = GetItemFromLocalstorage("spSettings");
  if (
    spSettings &&
    typeof spSettings === "object" &&
    (spSettings.ai_org_permissions || spSettings.ai_enabled !== undefined)
  ) {
    return parseOrgVertex360CopilotAiPayload(spSettings);
  }

  const user = GetItemFromLocalstorage("user");
  const serviceProvider = user?.service_provider;
  if (
    serviceProvider &&
    typeof serviceProvider === "object" &&
    (serviceProvider.ai_org_permissions || serviceProvider.ai_enabled !== undefined)
  ) {
    return parseOrgVertex360CopilotAiPayload(serviceProvider);
  }

  return null;
};

export const readOrgAiFromSpSettings = () => {
  const spSettings = GetItemFromLocalstorage("spSettings");
  if (!spSettings || typeof spSettings !== "object") {
    return null;
  }
  if (!spSettings.ai_org_permissions && spSettings.ai_enabled === undefined) {
    return null;
  }
  return parseOrgAiPayload(spSettings);
};

/**
 * Org AI gates from localStorage (login service_provider or spSettings after admin save).
 * Does not call sp/ai/settings — use that API only on the AI Settings page.
 */
export const readOrgAiFromServiceProvider = () => {
  const fromSpSettings = readOrgAiFromSpSettings();
  if (fromSpSettings) {
    return fromSpSettings;
  }

  const user = GetItemFromLocalstorage("user");
  const serviceProvider = user?.service_provider;
  if (!serviceProvider || typeof serviceProvider !== "object") {
    return null;
  }
  if (
    !serviceProvider.ai_org_permissions &&
    serviceProvider.ai_enabled === undefined
  ) {
    return null;
  }
  return parseOrgAiPayload(serviceProvider);
};

/**
 * Care Watch is visible when the organisation turns it on. Timesheet Anomaly
 * check is a separate switch and does not hide this. Managers also need the
 * role permission. Service provider admins follow the organisation switch.
 */
export const isCareWatchVisible = () => {
  if (!isAiFeaturesEnabled) return false;
  const org = readOrgAiFromServiceProvider();
  if (!org?.ai_enabled || !org?.orgCareWatch) return false;
  if (isSpAdminPortalUser()) return true;
  return (
    getSpPermissionStatus(AI_PERMISSIONS_PARENT) &&
    getSpPermissionStatus(AI_PERMISSIONS_PARENT, ROLE_AI_CARE_WATCH)
  );
};

export const isSpAdminPortalUser = () => {
  const userType = localStorage.getItem("userType");
  return userType === USER_ROLE.Sp_Admin;
};

/**
 * Merge org AI keys into localStorage.spSettings (AI Settings save / login shape).
 *
 * @param {Record<string, unknown>} orgPayload
 */
export const mergeOrgAiIntoSpSettings = (orgPayload) => {
  if (!orgPayload || typeof orgPayload !== "object") {
    return;
  }

  const hasAiEnabled = orgPayload.ai_enabled !== undefined;
  const hasOrgPermissions = orgPayload.ai_org_permissions !== undefined;
  if (!hasAiEnabled && !hasOrgPermissions) {
    return;
  }

  const raw = localStorage.getItem("spSettings");
  let spSettings = {};
  try {
    spSettings = raw ? JSON.parse(raw) : {};
  } catch {
    spSettings = {};
  }

  const next = { ...spSettings };
  if (hasAiEnabled) {
    next.ai_enabled = orgPayload.ai_enabled;
  }
  if (hasOrgPermissions) {
    next.ai_org_permissions = orgPayload.ai_org_permissions;
  }

  localStorage.setItem("spSettings", JSON.stringify(next));

  const user = GetItemFromLocalstorage("user");
  if (user && typeof user === "object" && user.service_provider) {
    const updatedServiceProvider = { ...user.service_provider };
    if (hasAiEnabled) {
      updatedServiceProvider.ai_enabled = orgPayload.ai_enabled;
    }
    if (hasOrgPermissions) {
      updatedServiceProvider.ai_org_permissions = orgPayload.ai_org_permissions;
    }
    localStorage.setItem(
      "user",
      JSON.stringify({
        ...user,
        service_provider: updatedServiceProvider,
      })
    );
  }

  window.dispatchEvent(new Event(SP_SETTINGS_AI_UPDATED_EVENT));
};

/**
 * Apply org AI gates from Pusher ai_org_update (role-sp-{sp_id} channel).
 *
 * @param {Record<string, unknown>|null|undefined} message
 */
export const mergeOrgAiFromPusherMessage = (message) => {
  if (!message || typeof message !== "object") {
    return false;
  }
  if (
    message.ai_enabled === undefined &&
    message.ai_org_permissions === undefined
  ) {
    return false;
  }
  mergeOrgAiIntoSpSettings({
    ai_enabled: message.ai_enabled,
    ai_org_permissions: message.ai_org_permissions,
  });
  return true;
};

const emptyOrg = () => ({
  loading: false,
  ai_enabled: false,
  orgTimesheetModule: false,
  orgCanAnalyse: false,
  orgCanBulkAnalyse: false,
  orgCanViewInsights: false,
});

/**
 * Layered timesheet AI access.
 * SP Admin: organisation gates only (service_provider / spSettings).
 * Managers: organisation + role permissions (user.permissions).
 *
 * @param {object} org — from parseOrgAiPayload + optional loading
 */
export const resolveTimesheetAiAccess = (org = {}) => {
  if (!isAiFeaturesEnabled) {
    return {
      loading: false,
      orgAiEnabled: false,
      roleAiParent: false,
      canAnalyse: false,
      canBulkAnalyse: false,
      canViewInsights: false,
      showAnyAiUi: false,
    };
  }

  if (org.loading) {
    return {
      loading: true,
      orgAiEnabled: false,
      roleAiParent: false,
      canAnalyse: false,
      canBulkAnalyse: false,
      canViewInsights: false,
      showAnyAiUi: false,
    };
  }

  const orgAiEnabled = coerceBool(org.ai_enabled);
  const orgCanAnalyse = coerceBool(org.orgCanAnalyse);
  const orgCanBulkAnalyse = coerceBool(org.orgCanBulkAnalyse);
  const orgCanViewInsights = coerceBool(org.orgCanViewInsights);

  if (isSpAdminPortalUser()) {
    const canAnalyse = orgAiEnabled && orgCanAnalyse;
    const canBulkAnalyse = canAnalyse && orgCanBulkAnalyse;
    const canViewInsights = orgAiEnabled && orgCanViewInsights;

    return {
      loading: false,
      orgAiEnabled,
      roleAiParent: true,
      canAnalyse,
      canBulkAnalyse,
      canViewInsights,
      showAnyAiUi: canAnalyse || canViewInsights,
    };
  }

  const roleAiParent = getSpPermissionStatus(AI_PERMISSIONS_PARENT);
  const roleCanAnalyse = getSpPermissionStatus(
    AI_PERMISSIONS_PARENT,
    ROLE_AI_ANOMALY_CHECK
  );
  const roleCanBulkAnalyse = getSpPermissionStatus(
    AI_PERMISSIONS_PARENT,
    ROLE_AI_BULK_ANOMALY_CHECK
  );
  const roleCanViewInsights = getSpPermissionStatus(
    AI_PERMISSIONS_PARENT,
    ROLE_AI_ANOMALY_READ
  );

  const canAnalyse =
    orgAiEnabled && orgCanAnalyse && roleAiParent && roleCanAnalyse;
  const canBulkAnalyse = canAnalyse && orgCanBulkAnalyse && roleCanBulkAnalyse;
  const canViewInsights =
    orgAiEnabled && orgCanViewInsights && roleAiParent && roleCanViewInsights;

  return {
    loading: false,
    orgAiEnabled,
    roleAiParent,
    canAnalyse,
    canBulkAnalyse,
    canViewInsights,
    showAnyAiUi: canAnalyse || canViewInsights,
  };
};

export const deniedOrgTimesheetAiAccess = () =>
  resolveTimesheetAiAccess(emptyOrg());

/**
 * Layered Copilot AI access.
 * SP Admin: organisation gates only (service_provider / spSettings).
 * Managers: organisation + `ai.copilot.query` role permission.
 *
 * @param {object} org — from parseOrgVertex360CopilotAiPayload + optional loading
 */
export const resolveVertex360CopilotAiAccess = (org = {}) => {
  if (!isAiFeaturesEnabled) {
    return { loading: false, orgAiEnabled: false, canUseVertex360Copilot: false };
  }

  if (org.loading) {
    return { loading: true, orgAiEnabled: false, canUseVertex360Copilot: false };
  }

  const orgAiEnabled = coerceBool(org.ai_enabled);
  const orgVertex360CopilotModule = coerceBool(org.orgVertex360CopilotModule);

  if (isSpAdminPortalUser()) {
    const canUseVertex360Copilot = orgAiEnabled && orgVertex360CopilotModule;
    return { loading: false, orgAiEnabled, roleAiParent: true, canUseVertex360Copilot };
  }

  const roleAiParent = getSpPermissionStatus(AI_PERMISSIONS_PARENT);
  const roleCopilot = getSpPermissionStatus(
    AI_PERMISSIONS_PARENT,
    ROLE_AI_VERTEX360_COPILOT_QUERY
  );

  const canUseVertex360Copilot =
    orgAiEnabled && orgVertex360CopilotModule && roleAiParent && roleCopilot;

  return { loading: false, orgAiEnabled, roleAiParent, canUseVertex360Copilot };
};

/**
 * Convenience gate for Copilot UI (FAB + /copilot workspace).
 * Requires org AI enabled + copilot module on + `ai.copilot.query` (admins bypass role).
 * Returns false when the org AI payload is not yet available.
 */
export const canUseVertex360Copilot = () => {
  const org = readOrgVertex360CopilotAiFromServiceProvider();
  if (!org) return false;
  return resolveVertex360CopilotAiAccess(org).canUseVertex360Copilot;
};
